import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type CSSProperties, type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject, useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { LineIcon } from '../icons/icons';
import { CAROUSEL_DURATION_MS, carouselEase } from '../tokens/motion';
import usePrefersReducedMotion from '../usePrefersReducedMotion';
import styles from './Carousel.module.scss';

/**
 * - `hero`: a media slide centred with its neighbours peeking at both sides,
 *   16:9 from md (the Home hero carousel).
 * - `card`: a card-width slide centred with its neighbours peeking, its
 *   controls in a row below (the testimonial panel).
 * - `wide`: one slide filling its frame, cross-fading (the professor
 *   carousels); the frame's height comes from the slot it sits in.
 * - `strip` (incubating, Phase 5 PR 5.4): a row of cards, three in view from
 *   md (one below), the current one centred, the controls in a row below (the
 *   /programs certificate strip). Every slide in view is interactive; the
 *   slides out of view are `inert`.
 */
export type CarouselVariant = 'hero' | 'card' | 'wide' | 'strip';
export type CarouselIndicators = 'numbered' | 'dots' | 'none';
export type CarouselControls = 'arrows' | 'none';
export type CarouselControlPlacement = 'sides' | 'below';
/**
 * Where the current slide's caption shows (`hero`, `wide`): over the media at
 * its `top` or `bottom`, or `below` it, in a row on the page under the
 * slides (Phase 6, the redesign's hero: the title and line under the media,
 * beside the page's `captionActions`).
 */
export type CarouselCaptionPlacement = 'top' | 'bottom' | 'below';
export type CarouselCaptionHeading = 'h2' | 'h3' | 'h4';
/**
 * The display role of the caption titles below the media
 * (`captionPlacement="below"`). `section`: the section display role (the
 * hero's default below the media, as every PageHero title). `band` (Phase 6,
 * the redesign's smaller hero title, 52px there): the band role, 42px at
 * every width, so the caption row is shorter. Unset, each variant keeps its
 * own (`hero` the section role, `wide` the card title role).
 */
export type CarouselCaptionTitleScale = 'section' | 'band';
/**
 * One caption title's element, over `captionHeading`. `h1` only where that
 * slide's caption is the page's title (the Home hero's first slide, its
 * caption `below`): one slide at most.
 */
export type CarouselCaptionTitleElement = 'h1' | CarouselCaptionHeading;
/**
 * The hero's media size. `compact` (Phase 6, the redesign's hero media): a
 * shorter frame, 390px tall below md and 996:540 from md, the slide at most
 * 996px wide (the redesign's content width), so the neighbours peek more.
 */
export type CarouselSize = 'default' | 'compact';
/**
 * The hero's slide corners. `hero`: the hero media radius, 16px below md and
 * 32px from md (`radius-hero-media`, `radius-3xl`), as Media's hero frame.
 */
export type CarouselRadius = 'none' | 'hero';
export interface CarouselCaption {
  /** The caption's title, rendered as `titleAs` or `captionHeading` (h3 by default). */
  title: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  /** This title's element, over the carousel's `captionHeading`. */
  titleAs?: CarouselCaptionTitleElement;
}
export interface CarouselSlide {
  content: ReactNode;
  /** Shown over or below the media while this slide is current (`hero`, `wide`). */
  caption?: CarouselCaption;
  /**
   * The accessible name of this slide's page dot (`indicators="dots"`).
   * Defaults to the caption title when that is text, else "Go to slide n of
   * N".
   */
  dotLabel?: string;
  key?: string;
}
export interface CarouselCardSlide {
  content: ReactNode;
  caption?: never;
  /**
   * The accessible name of this slide's page dot (`indicators="dots"`): a
   * card has no caption to name it (TestimonialCarousel passes the byline).
   * Defaults to "Go to slide n of N".
   */
  dotLabel?: string;
  key?: string;
}
export interface CarouselControlLabels {
  previous: string;
  next: string;
}
interface CarouselCommonProps {
  /** The carousel's accessible name (`aria-label` on the carousel group). */
  label: string;
  /**
   * Moves to the next slide after this many milliseconds: one duration for
   * every slide, or one per slide (`[20000, 5000, 5000, 5000]`: the first
   * stays longer). An array shorter than the slides gives the rest its last
   * value; a slide whose duration is 0 stays until it is moved. Off when
   * omitted or 0, under `prefers-reduced-motion` and for automated browsers
   * (`navigator.webdriver`). There is no pause/play control (O299 B, Alex,
   * 2026-10-04). Rotation holds, as the redesign's carousel does, while a
   * pointer is pressed on the carousel (a slide, a dot, an arrow: any pointer
   * type, until its release), while it is dragged, while it is out of view,
   * while the document is hidden, while `autoAdvancePaused` is set, and while
   * keyboard focus (`:focus-visible`) is anywhere inside it; hover never
   * holds it, nor does a pointer's focus. A hold pauses the current slide's
   * time and its dot's fill where they are, and its release resumes both; a
   * move (an arrow, a dot, a swipe, or the auto-advance itself) gives the new
   * slide its full time.
   */
  autoAdvanceMs?: number | readonly number[];
  /**
   * Holds auto-advance while true, as a press or keyboard focus does: the
   * slide's time pauses and resumes where it stopped when it is set back (the
   * Home hero holds while its video modal is open). Defaults to `false`.
   */
  autoAdvancePaused?: boolean;
  /**
   * `numbered`: numbered slide buttons ("01", "02", …), over the media on
   * `hero` and `wide`, between previous and next on `card` and `strip`.
   * `dots`: one unnumbered dot per slide in a row under the slides on every
   * variant, the current one a wider pill; with auto-advance it stretches
   * into a track that fills over the slide's duration. Defaults to `none`.
   */
  indicators?: CarouselIndicators;
  /** Previous and next buttons. Defaults to `arrows`. */
  controls?: CarouselControls;
  /** Accessible names of the previous and next buttons. */
  controlLabels?: CarouselControlLabels;
}
interface CarouselMediaBaseProps extends CarouselCommonProps {
  /** Defaults to `hero`. */
  variant?: 'hero' | 'wide';
  slides: readonly CarouselSlide[];
  /** The caption title's element. Defaults to `h3`. */
  captionHeading?: CarouselCaptionHeading;
  /** The hero's media size. Defaults to `default`; `wide` takes its slot's. */
  size?: CarouselSize;
  /** The hero's slide corners. Defaults to `none`. */
  radius?: CarouselRadius;
  /**
   * Where previous and next sit. `sides` (the default): over the media,
   * mid-height, at its sides. `below`: on the page, in the row under the
   * slides, previous at its start and next at its end, with the page dots
   * centred between them (or on a line of their own above them where they
   * do not fit), as the card and strip draw theirs; the slides carry no
   * control. The tab order is the same either way.
   */
  controlPlacement?: CarouselControlPlacement;
}

/** The caption over the media (`top`, or `bottom`, the default). */
interface CarouselOverlayCaptionProps {
  captionPlacement?: 'top' | 'bottom';
  captionActions?: never;
  captionTitleScale?: never;
}

/**
 * The caption `below` the media: the current slide's title and line in a
 * row under the slides (the hero's row is as wide as its slide), with
 * `captionActions` beside them from md and under them below md. The row
 * keeps the height of the longest caption, so the page does not move as the
 * slides turn. With `indicators="dots"` the dots sit under the row.
 */
interface CarouselBelowCaptionProps {
  captionPlacement: 'below';
  /** The page's calls to action in the caption row (they stay as the slides turn). */
  captionActions?: ReactNode;
  /** The caption titles' display role. Defaults to the variant's own. */
  captionTitleScale?: CarouselCaptionTitleScale;
}
export type CarouselMediaProps = CarouselMediaBaseProps & (CarouselOverlayCaptionProps | CarouselBelowCaptionProps);
export interface CarouselCardProps extends CarouselCommonProps {
  /** `strip`: three slides in view from md, one below (incubating). */
  variant: 'card' | 'strip';
  slides: readonly CarouselCardSlide[];
  captionPlacement?: never;
  captionHeading?: never;
  captionActions?: never;
  captionTitleScale?: never;
  size?: never;
  radius?: never;
  /** The card and strip always draw previous and next in their row below. */
  controlPlacement?: never;
}
export type CarouselProps = CarouselMediaProps | CarouselCardProps;
const DEFAULT_CONTROL_LABELS: CarouselControlLabels = {
  previous: 'Previous slide',
  next: 'Next slide'
};

/**
 * The most dots the stylesheet's card and strip rules size one by one
 * (`$dots-count-max` in Carousel.module.scss): past it the dot row says
 * "many" and always takes a line of its own above the arrows.
 */
const DOT_COUNT_MAX = 24;

/** A drag shorter than this share of a slide snaps back. */
const DRAG_COMMIT_THRESHOLD = 0.15;
/** Pixels a pointer travels before it counts as a drag rather than a click. */
const DRAG_START_DISTANCE = 8;
type MoveSource = 'user' | 'auto';

/**
 * The auto-advance duration of slide `index`: the number itself, or the
 * array's entry for the slide (its last entry past its end). 0 when there is
 * none: no number, an empty array, or a value that is not a positive finite
 * number.
 */
export function slideAutoAdvanceMs(autoAdvanceMs: number | readonly number[] | undefined, index: number) {
  const value = typeof autoAdvanceMs === 'number' ? autoAdvanceMs : autoAdvanceMs?.[Math.min(index, autoAdvanceMs.length - 1)];
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/** `value` modulo `count`, in [0, count). */
function modulo(value: number, count: number) {
  return (value % count + count) % count;
}

/**
 * A slide's distance from the visible position, in slides, wrapped into
 * [-count / 2, count / 2): the loop. A slide crosses from one end of the
 * range to the other when it is half the set away, out of view.
 */
export function wrapOffset(distance: number, count: number) {
  if (count <= 1) return 0;
  const offset = modulo(distance, count);
  return offset >= count / 2 ? offset - count : offset;
}
function slideVars(index: number, position: number, count: number) {
  const offset = wrapOffset(index - position, count);
  const presence = Math.max(0, 1 - Math.abs(offset));
  return {
    offset: Number(offset.toFixed(4)),
    presence: Number(presence.toFixed(4))
  };
}
function subscribeToNothing() {
  return () => undefined;
}
function isAutomatedBrowser() {
  return Boolean(window.navigator.webdriver);
}
function subscribeToVisibility(onChange: () => void) {
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}
function isDocumentHidden() {
  return document.hidden;
}

/**
 * The share of the carousel that must show for it to count as in view: the
 * redesign carousel's IntersectionObserver threshold. As there, it reads the
 * entry's `isIntersecting`, so rotation holds once the carousel is wholly out
 * of view and resumes as soon as any of it is back.
 */
const IN_VIEW_THRESHOLD = 0.05;

/**
 * Whether the carousel is in the viewport (an IntersectionObserver on its
 * root, observed while `rendered`). In view where the observer is missing,
 * and on the server.
 */
function useInView(rootRef, rendered) {
  const $ = _c(4);
  const [inView, setInView] = useState(true);
  let t0;
  let t1;
  if ($[0] !== rendered || $[1] !== rootRef) {
    t0 = () => {
      const root = rootRef.current;
      if (!rendered || !root || typeof window.IntersectionObserver === "undefined") {
        return;
      }
      const observer = new window.IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        if (entry) {
          setInView(entry.isIntersecting);
        }
      }, {
        threshold: IN_VIEW_THRESHOLD
      });
      observer.observe(root);
      return () => observer.disconnect();
    };
    t1 = [rendered, rootRef];
    $[0] = rendered;
    $[1] = rootRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return inView;
}

/**
 * Whether a pointer is pressed on the carousel (the redesign's
 * `pointerPaused`): from a primary pointerdown inside it, of any pointer
 * type, until that pointer's pointerup, pointercancel or lostpointercapture,
 * wherever the pointer is by then. A pointerup lets go one task later, so the
 * click it ends (on a dot or an arrow) lands while the press still holds.
 * Several pointers hold until the last one lets go.
 */
function usePointerPress() {
  const $ = _c(8);
  const [pressed, setPressed] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const pointersRef = useRef(t0);
  const releaseTimerRef = useRef(0);
  const detachRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (pointerId, deferred) => {
      const pointers = pointersRef.current;
      if (!pointers.delete(pointerId) || pointers.size > 0) {
        return;
      }
      detachRef.current?.();
      detachRef.current = null;
      window.clearTimeout(releaseTimerRef.current);
      if (deferred) {
        releaseTimerRef.current = window.setTimeout(() => setPressed(false), 0);
      } else {
        setPressed(false);
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const release = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      if (event.button !== 0) {
        return;
      }
      window.clearTimeout(releaseTimerRef.current);
      pointersRef.current.add(event.pointerId);
      setPressed(true);
      if (detachRef.current) {
        return;
      }
      const owner = event.currentTarget.ownerDocument;
      const onUp = up => release(up.pointerId, true);
      const onCancel = cancel => release(cancel.pointerId, false);
      owner.addEventListener("pointerup", onUp, true);
      owner.addEventListener("pointercancel", onCancel, true);
      detachRef.current = () => {
        owner.removeEventListener("pointerup", onUp, true);
        owner.removeEventListener("pointercancel", onCancel, true);
      };
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onPointerDownCapture = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event_0 => release(event_0.pointerId, false);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onLostPointerCapture = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      detachRef.current?.();
      detachRef.current = null;
      window.clearTimeout(releaseTimerRef.current);
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== pressed) {
    t6 = {
      pressed,
      onPointerDownCapture,
      onLostPointerCapture
    };
    $[6] = pressed;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function isEditable(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName));
}

/**
 * Whether focus arrived from the keyboard, by the browser's own heuristic
 * (`:focus-visible`: a pointer's focus does not match). Where the selector is
 * unsupported, assume it did: stopping rotation is the safe side.
 */
function isKeyboardFocus(target: EventTarget) {
  if (!(target instanceof Element)) return false;
  try {
    return target.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * A looping carousel: slides move by whole steps, with numbered or dot
 * indicators, previous/next buttons, captions over media, and auto-advance
 * that pauses while a pointer is pressed on it, while it is out of view and
 * while keyboard focus is inside it, and resumes where it stopped (no pause
 * control: O299 B). Arrow keys on its controls move it; a live region
 * announces the slide after a user's move only (never on auto-advance);
 * slides that are not current are `inert`; a pointer drag moves it too. Every slide is a real DOM node (no clones): each one is
 * placed by its own offset, so a slide leaves one edge and re-enters at the
 * other while it is out of view.
 */
function Carousel(props) {
  const $ = _c(159);
  const {
    label,
    autoAdvanceMs,
    autoAdvancePaused: t0,
    indicators: t1,
    controls: t2,
    controlLabels: t3
  } = props;
  const autoAdvancePaused = t0 === undefined ? false : t0;
  const indicators = t1 === undefined ? "none" : t1;
  const controls = t2 === undefined ? "arrows" : t2;
  const controlLabels = t3 === undefined ? DEFAULT_CONTROL_LABELS : t3;
  const variant = props.variant ?? "hero";
  const slides = props.slides;
  const onPage = props.variant === "card" || props.variant === "strip";
  const captionPlacement = onPage ? "bottom" : props.captionPlacement ?? "bottom";
  const CaptionHeading = onPage ? "h3" : props.captionHeading ?? "h3";
  const captionActions = onPage ? undefined : props.captionActions;
  const captionTitleScale = onPage || props.captionPlacement !== "below" ? undefined : props.captionTitleScale;
  const size = !onPage && props.size || "default";
  const radius = !onPage && props.radius || "none";
  const count = slides.length;
  const baseId = useId();
  let t4;
  if ($[0] !== baseId) {
    t4 = index => `${baseId}-slide-${index}`;
    $[0] = baseId;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const slideId = t4;
  const [index_0, setIndex] = useState(0);
  const [restIndex, setRestIndex] = useState(0);
  const [moving, setMoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [keyboardFocusWithin, setKeyboardFocusWithin] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const reducedMotion = usePrefersReducedMotion();
  const automated = useSyncExternalStore(subscribeToNothing, isAutomatedBrowser, _temp);
  const documentHidden = useSyncExternalStore(subscribeToVisibility, isDocumentHidden, _temp2);
  const rootRef = useRef(null);
  const inView = useInView(rootRef, count > 0);
  const press = usePointerPress();
  const lastPressedRef = useRef(null);
  const viewportRef = useRef(null);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const slideRefs = useRef(t5);
  const positionRef = useRef(0);
  const targetRef = useRef(0);
  const animationRef = useRef(null);
  const frameRef = useRef(0);
  const reducedMotionRef = useRef(reducedMotion);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const hasMultiple = count > 1;
  const current = count > 0 ? modulo(index_0, count) : 0;
  const rest = count > 0 ? modulo(restIndex, count) : 0;
  const durationMs = slideAutoAdvanceMs(autoAdvanceMs, current);
  const autoAdvance = durationMs > 0 && hasMultiple && !reducedMotion;
  const running = autoAdvance && !automated && !autoAdvancePaused && !press.pressed && !dragging && inView && !documentHidden && !keyboardFocusWithin;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      slide: -1,
      duration: 0,
      remaining: 0
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const clockRef = useRef(t6);
  let t7;
  if ($[4] !== count) {
    t7 = position => {
      slideRefs.current.forEach((element, slideIndex) => {
        if (!element) {
          return;
        }
        const {
          offset,
          presence
        } = slideVars(slideIndex, position, count);
        element.style.setProperty("--helios-carousel-offset", String(offset));
        element.style.setProperty("--helios-carousel-presence", String(presence));
      });
    };
    $[4] = count;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const paint = t7;
  let t8;
  if ($[6] !== count || $[7] !== paint) {
    t8 = position_0 => {
      const settled = modulo(Math.round(position_0), count);
      positionRef.current = settled;
      targetRef.current = settled;
      paint(settled);
      setRestIndex(settled);
      setMoving(false);
    };
    $[6] = count;
    $[7] = paint;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const settle = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = 0;
      animationRef.current = null;
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const stopAnimation = t9;
  let t10;
  if ($[10] !== count || $[11] !== hasMultiple || $[12] !== paint || $[13] !== settle) {
    t10 = (target, source) => {
      if (!hasMultiple) {
        return;
      }
      const nextIndex = modulo(target, count);
      targetRef.current = target;
      setIndex(nextIndex);
      setAnnouncement(source === "user" ? `Slide ${nextIndex + 1} of ${count}` : "");
      const from = positionRef.current;
      stopAnimation();
      if (reducedMotionRef.current || from === target) {
        settle(target);
        return;
      }
      const animation = {
        from,
        to: target,
        duration: CAROUSEL_DURATION_MS * Math.min(1, Math.max(0.35, Math.abs(target - from))),
        start: null as number | null
      };
      animationRef.current = animation;
      setMoving(true);
      function frame(time) {
        if (animationRef.current !== animation) {
          return;
        }
        if (animation.start === null) {
          animation.start = time;
        }
        const progress = reducedMotionRef.current || animation.duration <= 0 ? 1 : Math.min(1, Math.max(0, (time - animation.start) / animation.duration));
        const position_1 = animation.from + (animation.to - animation.from) * carouselEase(progress);
        positionRef.current = position_1;
        paint(position_1);
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(frame);
          return;
        }
        animationRef.current = null;
        frameRef.current = 0;
        settle(animation.to);
      }
      frameRef.current = requestAnimationFrame(frame);
    };
    $[10] = count;
    $[11] = hasMultiple;
    $[12] = paint;
    $[13] = settle;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const moveTo = t10;
  let t11;
  if ($[15] !== moveTo) {
    t11 = (direction, source_0) => {
      moveTo(targetRef.current + direction, source_0);
    };
    $[15] = moveTo;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const moveBy = t11;
  let t12;
  if ($[17] !== count || $[18] !== moveTo) {
    t12 = (slideIndex_0, source_1) => {
      const from_0 = targetRef.current;
      moveTo(from_0 + wrapOffset(slideIndex_0 - from_0, count), source_1);
    };
    $[17] = count;
    $[18] = moveTo;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const moveToSlide = t12;
  let t13;
  let t14;
  if ($[20] !== reducedMotion) {
    t13 = () => {
      reducedMotionRef.current = reducedMotion;
    };
    t14 = [reducedMotion];
    $[20] = reducedMotion;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => () => stopAnimation();
    t16 = [stopAnimation];
    $[23] = t15;
    $[24] = t16;
  } else {
    t15 = $[23];
    t16 = $[24];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[25] !== autoAdvance || $[26] !== current || $[27] !== durationMs || $[28] !== moveBy || $[29] !== running) {
    t17 = () => {
      const clock = clockRef.current;
      if (!autoAdvance || clock.slide !== current || clock.duration !== durationMs) {
        clock.slide = current;
        clock.duration = durationMs;
        clock.remaining = durationMs;
      }
      if (!running) {
        return;
      }
      const startedAt = performance.now();
      const timer = window.setTimeout(() => moveBy(1, "auto"), clock.remaining);
      return () => {
        window.clearTimeout(timer);
        clock.remaining = Math.max(0, clock.remaining - (performance.now() - startedAt));
      };
    };
    t18 = [autoAdvance, current, durationMs, moveBy, running];
    $[25] = autoAdvance;
    $[26] = current;
    $[27] = durationMs;
    $[28] = moveBy;
    $[29] = running;
    $[30] = t17;
    $[31] = t18;
  } else {
    t17 = $[30];
    t18 = $[31];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const onPointerDown = event => {
        lastPressedRef.current = event.target instanceof Element ? event.target : null;
      };
      document.addEventListener("pointerdown", onPointerDown, true);
      return () => document.removeEventListener("pointerdown", onPointerDown, true);
    };
    t20 = [];
    $[32] = t19;
    $[33] = t20;
  } else {
    t19 = $[32];
    t20 = $[33];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[34] !== current || $[35] !== reducedMotion) {
    t21 = () => {
      slideRefs.current.forEach((element_0, slideIndex_1) => {
        element_0?.querySelectorAll("video").forEach(video => {
          if (slideIndex_1 === current && !reducedMotion) {
            video.play?.()?.catch?.(_temp3);
          } else {
            video.pause?.();
          }
        });
      });
    };
    t22 = [current, reducedMotion];
    $[34] = current;
    $[35] = reducedMotion;
    $[36] = t21;
    $[37] = t22;
  } else {
    t21 = $[36];
    t22 = $[37];
  }
  useEffect(t21, t22);
  if (count === 0) {
    return null;
  }
  let t23;
  if ($[38] !== hasMultiple || $[39] !== moveBy) {
    t23 = event_0 => {
      if (!hasMultiple || event_0.defaultPrevented || isEditable(event_0.target)) {
        return;
      }
      if (event_0.target instanceof Element && event_0.target.closest("[data-helios-carousel-slide]")) {
        return;
      }
      if (event_0.key === "ArrowLeft") {
        event_0.preventDefault();
        moveBy(-1, "user");
      } else {
        if (event_0.key === "ArrowRight") {
          event_0.preventDefault();
          moveBy(1, "user");
        }
      }
    };
    $[38] = hasMultiple;
    $[39] = moveBy;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const handleKeyDown = t23;
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = event_1 => {
      const {
        target: target_0
      } = event_1;
      let keyboard = isKeyboardFocus(target_0);
      const pressed = lastPressedRef.current;
      if (keyboard && pressed && target_0 instanceof Node && target_0.contains(pressed)) {
        keyboard = false;
        lastPressedRef.current = null;
      }
      setKeyboardFocusWithin(keyboard);
    };
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const handleFocus = t24;
  let t25;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = event_2 => {
      const next = event_2.relatedTarget;
      if (!(next instanceof Node) || !event_2.currentTarget.contains(next)) {
        setKeyboardFocusWithin(false);
      }
    };
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  const handleBlur = t25;
  let t26;
  if ($[43] !== current || $[44] !== variant) {
    t26 = () => {
      const viewport = viewportRef.current;
      if (variant === "wide") {
        return viewport?.getBoundingClientRect().width ?? 0;
      }
      const slide = slideRefs.current[current];
      if (!slide) {
        return 0;
      }
      const gap = Number.parseFloat(getComputedStyle(slide).getPropertyValue("--helios-carousel-gap"));
      return slide.getBoundingClientRect().width + (Number.isFinite(gap) ? gap : 0);
    };
    $[43] = current;
    $[44] = variant;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const measureStep = t26;
  let t27;
  if ($[46] !== hasMultiple) {
    t27 = event_3 => {
      if (!hasMultiple || event_3.button !== 0 || isEditable(event_3.target)) {
        return;
      }
      dragRef.current = {
        pointerId: event_3.pointerId,
        x: event_3.clientX,
        y: event_3.clientY,
        startPosition: positionRef.current,
        step: 0,
        dragging: false
      };
    };
    $[46] = hasMultiple;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  const handlePointerDown = t27;
  let t28;
  if ($[48] !== measureStep || $[49] !== paint) {
    t28 = event_4 => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event_4.pointerId) {
        return;
      }
      const dx = event_4.clientX - drag.x;
      const dy = event_4.clientY - drag.y;
      if (!drag.dragging) {
        if (Math.abs(dy) > DRAG_START_DISTANCE && Math.abs(dy) >= Math.abs(dx)) {
          dragRef.current = null;
          return;
        }
        if (Math.abs(dx) < DRAG_START_DISTANCE) {
          return;
        }
        const slideStep = measureStep();
        if (slideStep < 1) {
          dragRef.current = null;
          return;
        }
        stopAnimation();
        drag.dragging = true;
        drag.step = slideStep;
        drag.startPosition = positionRef.current;
        drag.x = event_4.clientX;
        drag.y = event_4.clientY;
        setDragging(true);
        setMoving(true);
        event_4.currentTarget.setPointerCapture?.(event_4.pointerId);
        return;
      }
      const position_2 = drag.startPosition - dx / drag.step;
      positionRef.current = position_2;
      paint(position_2);
    };
    $[48] = measureStep;
    $[49] = paint;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  const handlePointerMove = t28;
  let t29;
  if ($[51] !== moveTo) {
    t29 = event_5 => {
      const drag_0 = dragRef.current;
      if (!drag_0 || drag_0.pointerId !== event_5.pointerId) {
        return;
      }
      dragRef.current = null;
      if (!drag_0.dragging) {
        return;
      }
      event_5.currentTarget.releasePointerCapture?.(event_5.pointerId);
      setDragging(false);
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      const travelled = positionRef.current - drag_0.startPosition;
      const start = Math.round(drag_0.startPosition);
      const target_1 = Math.abs(travelled) < DRAG_COMMIT_THRESHOLD ? start : start + Math.sign(travelled) * Math.max(1, Math.round(Math.abs(travelled)));
      moveTo(target_1, "user");
    };
    $[51] = moveTo;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  const endDrag = t29;
  const showControls = hasMultiple && controls === "arrows";
  const showIndicators = hasMultiple && indicators === "numbered";
  const showDots = hasMultiple && indicators === "dots";
  const showTimer = showDots && autoAdvance && !automated;
  const media = variant !== "card" && variant !== "strip";
  let t30;
  if ($[53] !== media || $[54] !== slides) {
    t30 = media && slides.some(_temp4);
    $[53] = media;
    $[54] = slides;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  const hasCaptions = t30;
  const below = media && captionPlacement === "below";
  const controlsBelow = media && showControls && props.controlPlacement === "below";
  let t31;
  if ($[56] !== baseId || $[57] !== controlLabels || $[58] !== moveBy || $[59] !== showControls) {
    t31 = showControls && <button type="button" className={classNames(styles["helios-carousel__control"], styles["helios-carousel__control--previous"])} aria-label={controlLabels.previous} aria-controls={`${baseId}-track`} data-helios-carousel-prev="" onClick={() => moveBy(-1, "user")}><LineIcon className={styles["helios-carousel__icon"]} name="chevron-previous" /></button>;
    $[56] = baseId;
    $[57] = controlLabels;
    $[58] = moveBy;
    $[59] = showControls;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  const previousButton = t31;
  let t32;
  if ($[61] !== baseId || $[62] !== controlLabels || $[63] !== moveBy || $[64] !== showControls) {
    t32 = showControls && <button type="button" className={classNames(styles["helios-carousel__control"], styles["helios-carousel__control--next"])} aria-label={controlLabels.next} aria-controls={`${baseId}-track`} data-helios-carousel-next="" onClick={() => moveBy(1, "user")}><LineIcon className={styles["helios-carousel__icon"]} name="chevron-next" /></button>;
    $[61] = baseId;
    $[62] = controlLabels;
    $[63] = moveBy;
    $[64] = showControls;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  const nextButton = t32;
  let t33;
  if ($[66] !== current || $[67] !== moveToSlide || $[68] !== showIndicators || $[69] !== slideId || $[70] !== slides) {
    t33 = showIndicators && slides.map((slide_1, slideIndex_2) => <button key={slide_1.key ?? slideIndex_2} type="button" className={styles["helios-carousel__indicator"]} aria-controls={slideId(slideIndex_2)} aria-current={slideIndex_2 === current ? "true" : undefined} data-helios-carousel-indicator="" data-index={slideIndex_2} data-current={slideIndex_2 === current ? "" : undefined} onClick={() => moveToSlide(slideIndex_2, "user")}>{String(slideIndex_2 + 1).padStart(2, "0")}</button>);
    $[66] = current;
    $[67] = moveToSlide;
    $[68] = showIndicators;
    $[69] = slideId;
    $[70] = slides;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  const indicatorButtons = t33;
  let t34;
  if ($[72] !== indicatorButtons || $[73] !== showIndicators) {
    t34 = showIndicators && <div className={styles["helios-carousel__meta"]}>{indicatorButtons}</div>;
    $[72] = indicatorButtons;
    $[73] = showIndicators;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  const meta = t34;
  let t35;
  if ($[75] !== CaptionHeading || $[76] !== below || $[77] !== current || $[78] !== hasCaptions || $[79] !== slides) {
    t35 = hasCaptions && slides.map((slide_2, slideIndex_3) => {
      if (!slide_2.caption) {
        return null;
      }
      const Title = slide_2.caption.titleAs ?? CaptionHeading;
      return <div key={slide_2.key ?? slideIndex_3} className={styles["helios-carousel__caption"]} hidden={below ? undefined : slideIndex_3 !== current} data-helios-carousel-caption="" data-index={slideIndex_3} data-current={slideIndex_3 === current ? "" : undefined}><Title className={styles["helios-carousel__caption-title"]}>{slide_2.caption.title}</Title>{slide_2.caption.description != null && <p className={styles["helios-carousel__caption-description"]}>{slide_2.caption.description}</p>}</div>;
    });
    $[75] = CaptionHeading;
    $[76] = below;
    $[77] = current;
    $[78] = hasCaptions;
    $[79] = slides;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const captions = t35;
  let t36;
  if ($[81] !== below || $[82] !== captionActions || $[83] !== captions || $[84] !== hasCaptions || $[85] !== meta || $[86] !== showIndicators) {
    t36 = below && (hasCaptions || showIndicators || captionActions) && <div className={styles["helios-carousel__below"]} data-helios-carousel-below=""><div className={styles["helios-carousel__captions"]}>{captions}{meta}</div>{captionActions != null && <div className={styles["helios-carousel__caption-actions"]}>{captionActions}</div>}</div>;
    $[81] = below;
    $[82] = captionActions;
    $[83] = captions;
    $[84] = hasCaptions;
    $[85] = meta;
    $[86] = showIndicators;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  const belowRow = t36;
  let t37;
  if ($[88] !== controlsBelow || $[89] !== count || $[90] !== current || $[91] !== durationMs || $[92] !== media || $[93] !== moveToSlide || $[94] !== showDots || $[95] !== showTimer || $[96] !== slideId || $[97] !== slides) {
    t37 = showDots && <div className={styles["helios-carousel__dots"]} data-helios-carousel-dots="" data-dot-count={media && !controlsBelow ? undefined : count > DOT_COUNT_MAX ? "many" : count}>{slides.map((slide_3, slideIndex_4) => {
        const isCurrent = slideIndex_4 === current;
        const title = slide_3.caption?.title;
        const dotLabel = slide_3.dotLabel?.trim();
        return <button key={slide_3.key ?? slideIndex_4} type="button" className={styles["helios-carousel__dot"]} aria-label={dotLabel || (media && typeof title === "string" && title.trim() ? title : `Go to slide ${slideIndex_4 + 1} of ${count}`)} aria-controls={slideId(slideIndex_4)} aria-current={isCurrent ? "true" : undefined} data-helios-carousel-dot="" data-index={slideIndex_4} data-current={isCurrent ? "" : undefined} data-timer={isCurrent && showTimer ? "" : undefined} onClick={() => moveToSlide(slideIndex_4, "user")}><span className={styles["helios-carousel__dot-mark"]} aria-hidden="true">{isCurrent && showTimer && <span key={durationMs} className={styles["helios-carousel__dot-fill"]} data-helios-carousel-dot-fill="" data-duration={durationMs} style={{
              "--helios-carousel-dot-duration": `${durationMs}ms`
            } as CSSProperties} />}</span></button>;
      })}</div>;
    $[88] = controlsBelow;
    $[89] = count;
    $[90] = current;
    $[91] = durationMs;
    $[92] = media;
    $[93] = moveToSlide;
    $[94] = showDots;
    $[95] = showTimer;
    $[96] = slideId;
    $[97] = slides;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  const dots = t37;
  const t38 = styles[`helios-carousel--${variant}`];
  const t39 = styles[`helios-carousel--caption-${captionPlacement}`];
  const t40 = showDots && styles["helios-carousel--dots"];
  const t41 = size !== "default" && styles[`helios-carousel--size-${size}`];
  const t42 = radius !== "none" && styles[`helios-carousel--radius-${radius}`];
  const t43 = controlsBelow && styles["helios-carousel--controls-below"];
  const t44 = captionTitleScale && styles[`helios-carousel--caption-title-${captionTitleScale}`];
  let t45;
  if ($[99] !== t38 || $[100] !== t39 || $[101] !== t40 || $[102] !== t41 || $[103] !== t42 || $[104] !== t43 || $[105] !== t44) {
    t45 = classNames(styles["helios-carousel"], t38, t39, t40, t41, t42, t43, t44);
    $[99] = t38;
    $[100] = t39;
    $[101] = t40;
    $[102] = t41;
    $[103] = t42;
    $[104] = t43;
    $[105] = t44;
    $[106] = t45;
  } else {
    t45 = $[106];
  }
  const t46 = "group";
  const t47 = "carousel";
  const t48 = "";
  const t49 = size !== "default" ? size : undefined;
  const t50 = radius !== "none" ? radius : undefined;
  const t51 = controlsBelow ? "below" : undefined;
  const t52 = autoAdvance ? running ? "running" : "paused" : "off";
  const t53 = moving ? "" : undefined;
  const t54 = press.onPointerDownCapture;
  const t55 = press.onLostPointerCapture;
  const t56 = media && dots;
  let t57;
  if ($[107] !== below || $[108] !== captions || $[109] !== dots || $[110] !== hasCaptions || $[111] !== media || $[112] !== meta || $[113] !== nextButton || $[114] !== previousButton || $[115] !== showControls || $[116] !== showIndicators) {
    t57 = media ? !below && (hasCaptions || showIndicators) && <div className={styles["helios-carousel__overlay"]}><div className={styles["helios-carousel__caption-block"]}>{meta}{captions}</div></div> : (showControls || meta || dots) && <div className={styles["helios-carousel__controls"]}>{meta}{dots}{previousButton}{nextButton}</div>;
    $[107] = below;
    $[108] = captions;
    $[109] = dots;
    $[110] = hasCaptions;
    $[111] = media;
    $[112] = meta;
    $[113] = nextButton;
    $[114] = previousButton;
    $[115] = showControls;
    $[116] = showIndicators;
    $[117] = t57;
  } else {
    t57 = $[117];
  }
  const t58 = styles["helios-carousel__viewport"];
  let t59;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = event_6 => {
      if (!suppressClickRef.current) {
        return;
      }
      suppressClickRef.current = false;
      event_6.preventDefault();
      event_6.stopPropagation();
    };
    $[118] = t59;
  } else {
    t59 = $[118];
  }
  const t60 = _temp5;
  const t61 = `${baseId}-track`;
  const t62 = styles["helios-carousel__track"];
  const t63 = slides.map((slide_4, slideIndex_5) => {
    const isCurrent_0 = slideIndex_5 === current;
    const inView_0 = isCurrent_0 || variant === "strip" && Math.abs(wrapOffset(slideIndex_5 - current, count)) <= 1;
    const {
      offset: offset_0,
      presence: presence_0
    } = slideVars(slideIndex_5, rest, count);
    return <div key={slide_4.key ?? slideIndex_5} ref={element_1 => {
      slideRefs.current[slideIndex_5] = element_1;
    }} id={slideId(slideIndex_5)} className={styles["helios-carousel__slide"]} role="group" aria-roledescription="slide" aria-label={`${slideIndex_5 + 1} of ${count}`} inert={!inView_0} data-helios-carousel-slide="" data-index={slideIndex_5} data-current={isCurrent_0 ? "" : undefined} data-in-view={variant === "strip" && inView_0 && !isCurrent_0 ? "" : undefined} style={{
      "--helios-carousel-offset": offset_0,
      "--helios-carousel-presence": presence_0
    } as CSSProperties}>{slide_4.content}</div>;
  });
  let t64;
  if ($[119] !== t61 || $[120] !== t62 || $[121] !== t63) {
    t64 = <div id={t61} className={t62}>{t63}</div>;
    $[119] = t61;
    $[120] = t62;
    $[121] = t63;
    $[122] = t64;
  } else {
    t64 = $[122];
  }
  let t65;
  if ($[123] !== endDrag || $[124] !== handlePointerDown || $[125] !== handlePointerMove || $[126] !== t58 || $[127] !== t59 || $[128] !== t60 || $[129] !== t64) {
    t65 = <div ref={viewportRef} className={t58} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} onClickCapture={t59} onDragStart={t60}>{t64}</div>;
    $[123] = endDrag;
    $[124] = handlePointerDown;
    $[125] = handlePointerMove;
    $[126] = t58;
    $[127] = t59;
    $[128] = t60;
    $[129] = t64;
    $[130] = t65;
  } else {
    t65 = $[130];
  }
  const t66 = controlsBelow && belowRow;
  let t67;
  if ($[131] !== media || $[132] !== nextButton || $[133] !== previousButton) {
    t67 = media && <>{previousButton}{nextButton}</>;
    $[131] = media;
    $[132] = nextButton;
    $[133] = previousButton;
    $[134] = t67;
  } else {
    t67 = $[134];
  }
  const t68 = !controlsBelow && belowRow;
  let t69;
  if ($[135] !== announcement) {
    t69 = <div className={styles["helios-carousel__live"]} aria-live="polite" aria-atomic="true" data-helios-carousel-live="">{announcement}</div>;
    $[135] = announcement;
    $[136] = t69;
  } else {
    t69 = $[136];
  }
  let t70;
  if ($[137] !== captionTitleScale || $[138] !== handleBlur || $[139] !== handleFocus || $[140] !== handleKeyDown || $[141] !== label || $[142] !== press.onLostPointerCapture || $[143] !== press.onPointerDownCapture || $[144] !== t45 || $[145] !== t49 || $[146] !== t50 || $[147] !== t51 || $[148] !== t52 || $[149] !== t53 || $[150] !== t56 || $[151] !== t57 || $[152] !== t65 || $[153] !== t66 || $[154] !== t67 || $[155] !== t68 || $[156] !== t69 || $[157] !== variant) {
    t70 = <div className={t45} role={t46} aria-roledescription={t47} aria-label={label} data-helios-carousel={t48} data-variant={variant} data-size={t49} data-radius={t50} data-control-placement={t51} data-caption-title-scale={captionTitleScale} data-auto-advance={t52} data-moving={t53} ref={rootRef} onKeyDown={handleKeyDown} onFocus={handleFocus} onBlur={handleBlur} onPointerDownCapture={t54} onLostPointerCapture={t55}>{t56}{t57}{t65}{t66}{t67}{t68}{t69}</div>;
    $[137] = captionTitleScale;
    $[138] = handleBlur;
    $[139] = handleFocus;
    $[140] = handleKeyDown;
    $[141] = label;
    $[142] = press.onLostPointerCapture;
    $[143] = press.onPointerDownCapture;
    $[144] = t45;
    $[145] = t49;
    $[146] = t50;
    $[147] = t51;
    $[148] = t52;
    $[149] = t53;
    $[150] = t56;
    $[151] = t57;
    $[152] = t65;
    $[153] = t66;
    $[154] = t67;
    $[155] = t68;
    $[156] = t69;
    $[157] = variant;
    $[158] = t70;
  } else {
    t70 = $[158];
  }
  return t70;
}
function _temp5(event_7) {
  return event_7.preventDefault();
}
function _temp4(slide_0) {
  return slide_0.caption;
}
function _temp3() {}
function _temp2() {
  return false;
}
function _temp() {
  return true;
}
export default Carousel;